'use client';

import React from 'react';
import { tokensToCssVariables } from './tokens';
import type { ThemeRendererProps } from './types';

/**
 * ThemeRenderer - Universal theme renderer for Route Royal Managed Websites
 * 
 * This component takes a theme configuration and renders it with customizations.
 * It handles CSS variable injection and renders theme components in the correct order.
 */
export function ThemeRenderer({ 
  theme, 
  customizations,
  tokens,
  data 
}: ThemeRendererProps) {
  const { components } = theme;
  
  // Sanitize and inject CSS variables for customization
  const sanitizeCSSValue = (value: unknown): string => {
    if (!value) return '';
    // Convert to string and remove any characters that could be used for XSS
    return String(value)
      .replace(/[<>]/g, '')
      .replace(/javascript:/gi, '')
      .replace(/expression\(/gi, '')
      .trim();
  };
  
  // Helper function to generate CSS variables from object
  const generateCSSVariables = (obj: Record<string, unknown>, prefix: string): string => {
    return Object.entries(obj)
      .map(([key, value]) => {
        const cssKey = `--${prefix}-${key}`;
        return `${cssKey}: ${sanitizeCSSValue(value)};`;
      })
      .join('\n      ');
  };

  // Build comprehensive CSS variables for branding
  const cssVariables = tokens 
    ? `:root {
      ${tokensToCssVariables(tokens)}
    }`
    : customizations ? `
    :root {
      /* Core colors */
      --color-primary: ${sanitizeCSSValue(customizations.colors.primary)};
      --color-secondary: ${sanitizeCSSValue(customizations.colors.secondary)};
      --color-accent: ${sanitizeCSSValue(customizations.colors.accent)};
      --color-background: ${sanitizeCSSValue(customizations.colors.background)};
      --color-surface: ${sanitizeCSSValue(customizations.colors.surface)};
      --color-text: ${sanitizeCSSValue(customizations.colors.text)};
      --color-text-muted: ${sanitizeCSSValue(customizations.colors.textMuted)};
      --color-border: ${sanitizeCSSValue(customizations.colors.border)};
      
      /* Semantic colors */
      ${customizations.colors.success ? `--color-success: ${sanitizeCSSValue(customizations.colors.success)};` : ''}
      ${customizations.colors.warning ? `--color-warning: ${sanitizeCSSValue(customizations.colors.warning)};` : ''}
      ${customizations.colors.error ? `--color-error: ${sanitizeCSSValue(customizations.colors.error)};` : ''}
      ${customizations.colors.info ? `--color-info: ${sanitizeCSSValue(customizations.colors.info)};` : ''}
      
      /* Gradients */
      ${customizations.colors.gradientStart && customizations.colors.gradientEnd 
        ? `--gradient-start: ${sanitizeCSSValue(customizations.colors.gradientStart)}; --gradient-end: ${sanitizeCSSValue(customizations.colors.gradientEnd)};` 
        : ''}
      
      /* Typography */
      --font-heading: "${sanitizeCSSValue(customizations.fonts.heading)}";
      --font-body: "${sanitizeCSSValue(customizations.fonts.body)}";
      ${customizations.fonts.headingWeight ? `--font-heading-weight: ${sanitizeCSSValue(customizations.fonts.headingWeight)};` : ''}
      ${customizations.fonts.bodyWeight ? `--font-body-weight: ${sanitizeCSSValue(customizations.fonts.bodyWeight)};` : ''}
      
      /* Typography Scale */
      ${customizations.typography?.fontSize ? generateCSSVariables(customizations.typography.fontSize, 'font-size') : ''}
      ${customizations.typography?.lineHeight ? generateCSSVariables(customizations.typography.lineHeight, 'line-height') : ''}
      ${customizations.typography?.letterSpacing ? generateCSSVariables(customizations.typography.letterSpacing, 'letter-spacing') : ''}
      
      /* Component Overrides */
      ${customizations.components?.button ? generateCSSVariables(customizations.components.button, 'button') : ''}
      ${customizations.components?.card ? generateCSSVariables(customizations.components.card, 'card') : ''}
      ${customizations.components?.input ? generateCSSVariables(customizations.components.input, 'input') : ''}
      
      /* Layout Configuration */
      ${customizations.layout?.maxWidth ? `--layout-max-width: ${sanitizeCSSValue(customizations.layout.maxWidth)};` : ''}
      ${customizations.layout?.containerPadding ? `--layout-container-padding: ${sanitizeCSSValue(customizations.layout.containerPadding)};` : ''}
      ${customizations.layout?.sidebar?.position ? `--layout-sidebar-position: ${sanitizeCSSValue(customizations.layout.sidebar.position)};` : ''}
      ${customizations.layout?.sidebar?.width ? `--layout-sidebar-width: ${sanitizeCSSValue(customizations.layout.sidebar.width)};` : ''}
      ${customizations.layout?.header?.fixed ? `--layout-header-fixed: ${customizations.layout.header.fixed ? '1' : '0'};` : ''}
      ${customizations.layout?.header?.height ? `--layout-header-height: ${sanitizeCSSValue(customizations.layout.header.height)};` : ''}
    }
  ` : '';
  
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: cssVariables }} />
      
      {/* Navigation is rendered by the layout (LayoutNavigation component) */}
      {/* We don't render it here to avoid duplication */}
      
      {/* Render Homepage Sections */}
      {theme.layoutConfig.homepage
        .sort((a, b) => a.order - b.order)
        .map((section, index) => {
          // Skip Navigation component as it's rendered separately above
          if (section.component === 'Navigation') {
            return null;
          }

          const Component = components[section.component] as React.ComponentType<Record<string, unknown>>;
          if (!Component) {
            console.warn(`Component ${section.component} not found in theme`);
            return null;
          }

          // Only spread props that match the component's expected props
          // Pass data, messages, and locale to all components that might need them
          return (
            <Component
              key={`${section.component}-${index}`}
              {...(section.props as Record<string, unknown> || {})}
              {...(data && { data })}
              {...(data?.messages && { messages: data.messages })}
              {...(data?.locale && { locale: data.locale })}
              {...(data?.resources && { properties: data.resources })}
            />
          );
        })}
      
      {/* Footer is rendered separately by the page component */}
      {/* Removed automatic footer rendering to allow custom page layouts */}
    </>
  );
}

